import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import TimeRangeSelector from './TimeRangeSelector';
import { subYears } from 'date-fns';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import ProductSelector from './ProductSelector';
import { getAssessmentKeywords } from '../utils/assessmentKeywords';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CompetitorChurnPlot = ({ posts, product_name, assessmentData, topics }) => {
    const [competitors, setCompetitors] = React.useState({});
    const [selectedCompetitors, setSelectedCompetitors] = React.useState([]);
    const [postsReferenced, setPostsReferenced] = React.useState([]);
    const [selectedCompetitor, setSelectedCompetitor] = React.useState(null);
    const [timeRange, setTimeRange] = React.useState('3y');
    const [selectedProduct, setSelectedProduct] = React.useState(null);
    const [filteredTopicMap, setFilteredTopicMap] = React.useState({});
    const [selectedAssessmentData, setSelectedAssessmentData] = React.useState([]);

    // Debug logging
    console.log('CompetitorChurnPlot props:', { 
        postsLength: posts?.length,
        product_name,
        assessmentDataLength: assessmentData?.length,
        topicsLength: topics?.length
    });

    // Filter topics to only include those with competitive comparison assessment data
    React.useEffect(() => {
        // Ensure it's empty if inputs are missing/invalid
        if (!topics || !Array.isArray(topics)) {
            setFilteredTopicMap({});
            console.log('CompetitorChurnPlot: Missing or invalid topics prop.');
            return;
        }
        
        // If no assessment data yet, show all topics
        if (!assessmentData || !Array.isArray(assessmentData) || assessmentData.length === 0) {
            console.log('CompetitorChurnPlot: No assessment data yet, showing all topics');
            const topicMap = topics.reduce((acc, topic) => {
                if (topic && topic.id) {
                    acc[topic.id] = {
                        id: topic.id,
                        name: topic.name || topic.topic, 
                        priority: topic.priority
                    };
                }
                return acc;
            }, {});
            setFilteredTopicMap(topicMap);
            return;
        }
        
        // Group assessmentData by topic_id
        const assessmentsByTopic = {};
        assessmentData.forEach(item => {
            // Ensure item and topic_id are valid
            if (item && item.topic_id) {
                if (!assessmentsByTopic[item.topic_id]) {
                    assessmentsByTopic[item.topic_id] = [];
                }
                assessmentsByTopic[item.topic_id].push(item);
            }
        });
        console.log('CompetitorChurnPlot: Assessments grouped by topic ID:', assessmentsByTopic);
        
        // Filter topics to only include those with assessment data
        const validTopics = topics.filter(topic => 
            // Ensure topic and topic.id are valid
            topic && topic.id &&
            assessmentsByTopic[topic.id] && assessmentsByTopic[topic.id].length > 0
        );
        console.log('CompetitorChurnPlot: Topics with associated assessment data:', validTopics);
        
        // Convert to topicMap format for ProductSelector
        const topicMap = validTopics.reduce((acc, topic) => {
            // Use topic.topic as fallback for name, like in ProductSelector
            acc[topic.id] = {
                id: topic.id,
                name: topic.name || topic.topic, 
                priority: topic.priority
            };
            return acc;
        }, {});
        
        console.log('CompetitorChurnPlot: Setting Filtered Topic Map:', topicMap);
        setFilteredTopicMap(topicMap);
    }, [topics, assessmentData]);

    // Filter assessment data based on selected product
    React.useEffect(() => {
        if (!assessmentData) {
            setSelectedAssessmentData([]);
            return;
        }
        
        if (!selectedProduct) {
            // If no product selected, use all assessment data
            setSelectedAssessmentData(assessmentData);
        } else {
            // Filter to selected product
            const filtered = assessmentData.filter(item => 
                item.topic_id === selectedProduct
            );
            setSelectedAssessmentData(filtered);
        }
    }, [assessmentData, selectedProduct]);
  
    React.useEffect(() => {
        const competitorCounts = {};
        const now = new Date();
        
        // Use filtered assessmentData if available, otherwise fall back to posts
        if (selectedAssessmentData && selectedAssessmentData.length > 0) {
            console.log('Using provided assessment data for competitor churn:', selectedAssessmentData.length);
            
            const filteredData = selectedAssessmentData.filter(item => {
                if (!item?.time) return false;
                if (timeRange === 'all') return true;
                const date = new Date(item.time * 1000);
                const years = parseInt(timeRange);
                return date >= subYears(now, years);
            });
            
            filteredData.forEach(item => {
                // Use comparator field instead of details.competitor
                const competitor = item.comparator || item.details?.competitor;
                if (competitor) {
                    if (!competitorCounts[competitor]) {
                        competitorCounts[competitor] = { total: 0, leaving: 0, joining: 0 };
                    }
                    competitorCounts[competitor].total += 1;
                    
                    if (item.rating < 0) {
                        competitorCounts[competitor].leaving += 1;
                    } else if (item.rating > 0) {
                        competitorCounts[competitor].joining += 1;
                    }
                }
            });
        } else {
            console.log('Falling back to filtering posts for competitor churn data');
            
            const filteredPosts = (posts || []).filter(post => {
                if (timeRange === 'all') return true;
                const date = new Date(post?.time * 1000);
                const years = parseInt(timeRange);
                return date >= subYears(now, years);
            });

            filteredPosts.forEach(item => {
                if (!item?.post_assessments) return;
                const assessment = item.post_assessments.find(a => a?.type === 'leaver_joiner');
                if (assessment?.details?.competitor) {
                    const competitor = assessment.details.competitor;
                    if (!competitorCounts[competitor]) {
                        competitorCounts[competitor] = { total: 0, leaving: 0, joining: 0 };
                    }
                    competitorCounts[competitor].total += 1;
                    
                    if (assessment.rating < 0) {
                        competitorCounts[competitor].leaving += 1;
                    } else if (assessment.rating > 0) {
                        competitorCounts[competitor].joining += 1;
                    }
                }
            });
        }

        const sortedCompetitors = Object.entries(competitorCounts)
            .sort((a, b) => b[1].total - a[1].total)
            .slice(0, 5);

        setCompetitors(Object.fromEntries(sortedCompetitors));
        setSelectedCompetitors(Object.keys(Object.fromEntries(sortedCompetitors)));
    }, [posts, selectedAssessmentData, timeRange]);

    const prepareChartData = () => {
        const sortedCompetitors = selectedCompetitors
            .sort((a, b) => {
                const totalA = (competitors[a]?.joining || 0) + Math.abs(competitors[a]?.leaving || 0);
                const totalB = (competitors[b]?.joining || 0) + Math.abs(competitors[b]?.leaving || 0);
                return totalB - totalA;
            });

        const labels = sortedCompetitors;
        const joiningData = labels.map(label => competitors[label]?.joining || 0);
        const leavingData = labels.map(label => -(competitors[label]?.leaving || 0));

        return {
            labels,
            datasets: [
                {
                    label: 'Joining',
                    data: joiningData,
                    backgroundColor: 'rgba(144, 238, 144, 0.7)',
                    borderColor: 'rgba(144, 238, 144, 1)',
                    borderWidth: 1,
                },
                {
                    label: 'Leaving',
                    data: leavingData,
                    backgroundColor: 'rgba(255, 99, 71, 0.7)',
                    borderColor: 'rgba(255, 99, 71, 1)',
                    borderWidth: 1,
                }
            ]
        };
    };

    const handleChartClick = (event, elements) => {
        if (elements.length > 0) {
            const { index, datasetIndex } = elements[0];
            const competitor = selectedCompetitors[index];
            setSelectedCompetitor(competitor);

            const isJoining = datasetIndex === 0;
            const isLeaving = datasetIndex === 1;
            
            if (isJoining || isLeaving) {
                let competitorPosts = [];
                
                if (selectedAssessmentData && selectedAssessmentData.length > 0) {
                    // Filter directly from assessment data
                    competitorPosts = selectedAssessmentData.filter(item => {
                        // Use comparator field instead of details.competitor
                        const itemCompetitor = item.comparator || item.details?.competitor;
                        if (!itemCompetitor || itemCompetitor !== competitor) {
                            return false;
                        }
                        return isJoining ? item.rating > 0 : item.rating < 0;
                    }).map(item => ({
                        ...item,
                        ratingName: 'Net Add Rating',
                        ratingType: 'leaver_joiner'
                    }));
                } else {
                    // Filter from original posts data
                    competitorPosts = (posts || []).filter(post => 
                        post?.post_assessments?.some(a => {
                            if (a?.type !== 'leaver_joiner' || 
                                a?.details?.competitor !== competitor) {
                                return false;
                            }
                            return isJoining ? a.rating > 0 : a.rating < 0;
                        })
                    )
                    .map(post => {
                        const assessment = post?.post_assessments?.find(a => 
                            a?.type === 'leaver_joiner' && 
                            a?.details?.competitor === competitor
                        );
                        return {
                            ...post,
                            rating: assessment?.rating,
                            confidence: assessment?.confidence,
                            explanation: assessment?.explanation,
                            ratingName: 'Net Add Rating',
                            ratingType: 'leaver_joiner'
                        };
                    });
                }
                
                setPostsReferenced(competitorPosts);
            }
        }
    };

    return (
        <div>
            {/* Title showing selected product */}
            {selectedProduct && filteredTopicMap[selectedProduct] && (
                <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '500' }}>
                    {filteredTopicMap[selectedProduct].name} - Competitive Losses / Wins
                </h3>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <ProductSelector
                        topicMap={filteredTopicMap}
                        selectedId={selectedProduct}
                        onSelect={setSelectedProduct}
                        label="Product"
                    />
                </div>
                <TimeRangeSelector
                    timeRange={timeRange}
                    onTimeRangeChange={setTimeRange}
                    periods={['1y', '3y', '5y', 'all']}
                />
            </div>
            <Bar
                data={prepareChartData()}
                options={{
                    responsive: true,
                    scales: {
                        x: {
                            stacked: true,
                            title: {
                                display: true,
                                text: 'Competitors'
                            }
                        },
                        y: {
                            stacked: true,
                            title: {
                                display: true,
                                text: 'Number of Mentions'
                            }
                        }
                    },
                    plugins: {
                        title: {
                            display: true,
                        },
                        tooltip: {
                            callbacks: {
                                label: function(context) {
                                    const value = Math.abs(context.parsed.y);
                                    const dataset = context.dataset.label;
                                    return `${dataset}: ${value}`;
                                }
                            }
                        }
                    },
                    onClick: handleChartClick,
                }}
            />

            <FilteredPostsDisplay
                posts={postsReferenced}
                onClose={() => setPostsReferenced([])}
                title="Referenced Posts"
                subtitle={selectedCompetitor ? `${product_name} vs. ${selectedCompetitor}` : null}
                ratingDisplayName="Net Add Rating"
                ratingColorScheme={{high: 0, low: 0}}
                autoScroll={true}
                query={getAssessmentKeywords('competitive_comparison', { competitor: selectedCompetitor })}
            />
        </div>
    );
};

export default CompetitorChurnPlot; 